import React, { useState } from 'react';
import { Box, Typography, TextField, IconButton, Button } from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { filterInput } from '../../utils/inputSanitization';

// Keycloak's two magic literals for post.logout.redirect.uris. "+" means "also accept every
// registered redirect URI" and "-" means "accept none". Prism decides those semantics itself
// from the post-logout mode selector, and the backend validator rejects both outright, so
// catching them here turns a round-trip 400 into an inline message that points at the control
// the admin actually wants.
export const RESERVED_URI_LITERALS = ['+', '-'];

export const DEFAULT_RESERVED_MESSAGE =
  '"+" and "-" are reserved values, not URLs. Enter a full https:// URL.';

// The wildcard rule, stated the way the backend enforces it. Worth spelling out the reason:
// without the slash, matching is a prefix match, so "https://app.example.com*" would also
// match "https://app.example.com.attacker.com".
export const WILDCARD_HELPER_TEXT =
  'A wildcard is allowed only as a trailing "/*" after a complete host — https://app.example.com/* is accepted, ' +
  'https://app.example.com* is not, because prefix matching would also match https://app.example.com.attacker.com.';

/*
 * Fragments, and the one workaround that actually works.
 *
 * Verified against a live Keycloak 26.7.1: a redirect URI containing "#" is rejected outright
 * with 400 "A redirect URI must not contain an URL fragment", and the backend validator now
 * rejects it before the request ever leaves Prism. A hash-routed SPA is not stuck, though —
 * a trailing wildcard prefix-matches the WHOLE incoming address, fragment included, so
 * "https://app.example.com/*" accepts "https://app.example.com/#/callback" with no change to
 * the application. Registering the bare origin "https://app.example.com/" does not: the match
 * is a prefix match against the full URL, and "/#/callback" is not a prefix of "/".
 */
export const FRAGMENT_HELPER_TEXT =
  'A URI must not contain a "#" fragment — it is rejected outright. For a hash-routed single-page ' +
  'application, register the trailing wildcard https://app.example.com/* instead: it matches the whole ' +
  'address including the fragment, so https://app.example.com/#/callback is accepted. The bare origin ' +
  'https://app.example.com/ does not work.';

/**
 * Per-row client-side check. Deliberately narrow: the backend validator is the authority on
 * URI syntax and returns field-indexed errors, so duplicating its rules here would only create
 * a second, drifting implementation. The one thing worth catching locally is a reserved
 * literal, because the fix for it is a different control rather than a different URL.
 *
 * Returns an array aligned with `rows`: '' when the row is acceptable, otherwise the message.
 */
export const validateUriRows = (rows, { reservedMessage } = {}) =>
  (Array.isArray(rows) ? rows : []).map((raw) => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return ''; // emptiness is the caller's `required` rule, not a row error
    if (RESERVED_URI_LITERALS.includes(trimmed)) return reservedMessage || DEFAULT_RESERVED_MESSAGE;
    return '';
  });

/**
 * Pull field-indexed backend errors back apart into per-row messages.
 *
 * The backend reports OIDC validation failures against an indexed field name
 * (`redirectUris[2]`, `postLogoutRedirectUris[0]`), but api.js flattens the whole error
 * response to a single Error message string, so the structure only survives inside the text.
 * Recovering the index is what lets the message sit against the row that caused it instead of
 * floating above a list of five identical-looking inputs.
 *
 * Returns an object keyed by row index. Empty when nothing matches, in which case the caller
 * should still surface the raw message somewhere — never swallow it.
 */
export const parseIndexedFieldErrors = (message, field) => {
  const out = {};
  if (!message || !field) return out;
  // The leading guard matters: "postLogoutRedirectUris[0]" ends with "redirectUris[0]", so an
  // unanchored search for the shorter field name would steal the longer field's errors.
  //
  // The capture runs to the newline and NOTHING ELSE, because the newline is the delimiter
  // the backend joins its validation errors with (strings.Join(parts, "\n") in
  // custom_application_handler.go) and it pins that with a test asserting no single
  // validation message may contain one. Delimiter and pattern are a matched pair:
  //   - It used to stop at ';' too. 31 of the 47 validation messages contain a semicolon
  //     mid-sentence, so the admin got the diagnosis and lost the remediation half after it.
  //   - It must not be relaxed to a printable delimiter like " | ". For the 16 messages with
  //     no internal punctuation to stop on, the capture would run straight through the
  //     separator and swallow the NEXT field's error, leaving row N+1 silently blank.
  const pattern = new RegExp(`(?:^|[^A-Za-z])${field}\\[(\\d+)\\]\\s*:?\\s*([^\\n]*)`, 'g');
  let match = pattern.exec(message);
  while (match !== null) {
    const index = Number(match[1]);
    const detail = (match[2] || '').trim();
    out[index] = detail || message;
    match = pattern.exec(message);
  }
  return out;
};

const rowErrorAt = (rowErrors, index) => {
  if (!rowErrors) return '';
  if (Array.isArray(rowErrors)) return rowErrors[index] || '';
  return rowErrors[index] || '';
};

/*
 * The verbs a backend validation message can open with when it is a bare PREDICATE.
 *
 * Nearly every message in backend/utils/oidc_validation.go is written as a field name followed
 * by a predicate -- `redirectUris[0]: must not contain a URL fragment ("#"); ...`. That shape
 * is deliberate on the backend side, because the field name is the subject. But
 * parseIndexedFieldErrors strips the field name (it has already used it to pick the row), so
 * what reaches the input is a sentence with its subject removed, rendered starting lowercase
 * and mid-thought: "must not contain a URL fragment ...". composeRowError puts the subject
 * back by prefixing the row's own label.
 *
 * Verified against every message ValidateOIDCRedirectURI and
 * ValidateOIDCPostLogoutRedirectURI can emit. The openers are: is, must, may, has, contains.
 */
const PREDICATE_OPENER = /^(?:is|must|may|has|contains)\b/;

/**
 * Give a row error back the subject the field name used to supply.
 *
 * NOT every message is a predicate. A handful name their own subject first, because the thing
 * at fault is a part of the URI rather than the URI itself:
 *
 *   "*" is not a redirect URI -- it permits a redirect to any host ...
 *   "+" is a reserved Keycloak value, not a URL ...
 *   the wildcard "*" must immediately follow a "/" ...
 *   the host "app.example.com" contains non-ASCII characters ...
 *
 * Prefixing those produces the mangled 'Redirect URI 1 "*" is not a redirect URI'. They are
 * capitalised and left to stand alone instead, which reads correctly as-is.
 *
 * The test is a WHITELIST of predicate openers rather than a blacklist of subjects, because
 * the two failure modes are not equal. An unrecognised predicate falls through to
 * capitalisation and merely keeps today's subject-less phrasing with a capital letter -- no
 * worse than the status quo. An unrecognised subject would produce the hybrid above. So the
 * branch that can embarrass us is the one that must be opted into explicitly.
 *
 * Client-side messages (DEFAULT_RESERVED_MESSAGE and friends) are authored as whole sentences
 * and already start with a quote or a capital, so they pass through untouched.
 */
export const composeRowError = (message, subject) => {
  const text = String(message ?? '').trim();
  if (!text) return '';
  if (subject && PREDICATE_OPENER.test(text)) return `${subject} ${text}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// A required list always shows a row to type into; an optional one may legitimately be empty.
const normalizeRows = (value, required) => {
  const list = Array.isArray(value) ? value : [];
  if (list.length > 0) return list;
  return required ? [''] : [];
};

/**
 * Add/remove list of URIs. Used for OIDC redirect URIs and, when the admin picks the explicit
 * post-logout mode, for post-logout redirect URIs.
 */
const RedirectUriListField = ({
  label,
  value,
  onChange,
  helperText,
  error,
  rowErrors,
  placeholder = 'https://app.example.com/callback',
  required = false,
  disabled = false,
  addLabel = 'Add URI',
  reservedMessage,
  sx,
}) => {
  /*
   * Backend row errors are indexed positions in the exact list that was submitted, so they
   * only mean anything while that list is still on screen. Editing a row makes its message
   * describe a URI the admin has already replaced, and deleting a row shifts every row after
   * it up one — the message then re-anchors onto an innocent URI that nothing is wrong with.
   *
   * So the whole set is dropped as soon as the admin edits or deletes: the backend is the
   * authority and it will say so again on the next save. What is remembered is the identity
   * of the rowErrors object they were dropped for, so a NEW object — the next failed save —
   * brings its messages back.
   *
   * Adding a row is deliberately not a dismissal. It appends, shifts no index and changes no
   * existing value, so every message still sits against the row it was computed for.
   */
  const [dismissedFor, setDismissedFor] = useState(null);
  const activeRowErrors = dismissedFor && dismissedFor.source === rowErrors ? null : rowErrors;

  const rows = normalizeRows(value, required);
  const clientErrors = validateUriRows(rows, { reservedMessage });
  const errorFor = (index) => rowErrorAt(activeRowErrors, index) || clientErrors[index] || '';

  // Every mutation that invalidates the backend's indexing goes through here.
  const mutate = (updated) => {
    setDismissedFor({ source: rowErrors });
    onChange(updated);
  };

  const setAt = (index, next) => {
    const updated = [...rows];
    updated[index] = next;
    mutate(updated);
  };

  const removeAt = (index) => mutate(rows.filter((_, i) => i !== index));

  const addRow = () => onChange([...rows, '']);

  // The last row of a required list is not removable — there is no valid state with zero rows,
  // and silently re-adding one after a delete reads as a broken button.
  const removeDisabled = disabled || (required && rows.length <= 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>
        {label}
        {required && <span style={{ color: '#E74C3C' }}> *</span>}
      </Typography>

      {rows.map((row, index) => {
        // The same string the input is labelled with, so the error names the row the admin is
        // looking at rather than inventing a second vocabulary for it.
        const rowLabel = `${label} ${index + 1}`;
        const rowError = composeRowError(errorFor(index), rowLabel);
        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              label={rowLabel}
              value={row}
              onChange={(e) => setAt(index, filterInput(e.target.value, 'URL'))}
              placeholder={placeholder}
              disabled={disabled}
              error={!!rowError}
              helperText={rowError || undefined}
              fullWidth
              size="small"
              sx={sx}
            />
            <IconButton
              aria-label={`Remove ${rowLabel}`}
              onClick={() => removeAt(index)}
              disabled={removeDisabled}
              size="small"
              sx={{ mt: 0.5, color: '#999999', '&:hover': { color: '#E74C3C' } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}

      <Box>
        <Button
          onClick={addRow}
          disabled={disabled}
          startIcon={<AddCircleOutlineIcon />}
          size="small"
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            textTransform: 'none',
            color: '#27AE60',
            '&:hover': { backgroundColor: '#F0F9F4' },
          }}
        >
          {addLabel}
        </Button>
      </Box>

      {(error || helperText) && (
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.75rem',
            color: error ? '#E74C3C' : '#999999',
          }}
        >
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RedirectUriListField;
